import DataTable from "@components/DataTable";
import type { ColumnDef } from "@tanstack/react-table";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Card } from "react-bootstrap";
import { faTrashCan, faSpinner } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import ConfirmationModal from "@components/Modals/ConfirmationModal";
import { useDeletePromptOverrideMutation } from "@generated";
import { useAuthorization } from "../../../../common/providers/AuthorizationProvider";
import useListPromptOverrides from "./api";
import { PromptOverride } from "./types";
import { PROMPT_TYPE_OPTIONS } from "../common/types";

const List = (): JSX.Element => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const authorized = useAuthorization();
  const [deletePromptOverride, {}] = useDeletePromptOverrideMutation();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const { promptOverrides, pageMeta, isLoading, refetch } =
    useListPromptOverrides({
      page: {
        number: pageNumber,
        size: pageSize,
      },
    });

  useEffect(() => {
    if ((location.state as any)?.refetch) {
      refetch();
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  const handleDelete = () => {
    setShowConfirmationModal(true);
  };

  const handleConfirm = () => {
    selectedIds.forEach((id) => {
      deletePromptOverride({
        slug: accountSlug,
        id: id as any,
      });
    });
    setSelectedIds([]);
    refetch();
    setShowConfirmationModal(false);
  };

  const handleCancel = () => {
    setShowConfirmationModal(false);
  };

  const getColumns = (): ColumnDef<PromptOverride>[] => {
    if (!authorized || isLoading) {
      return [
        {
          header: "Prompt Type",
          cell() {
            return <FontAwesomeIcon icon={faSpinner} spin />;
          },
        },
        {
          header: "LLM Key",
          cell() {
            return <FontAwesomeIcon icon={faSpinner} spin />;
          },
        },
        {
          header: "Content",
          cell() {
            return <FontAwesomeIcon icon={faSpinner} spin />;
          },
        },
      ];
    }

    if (promptOverrides.length === 0) {
      return [
        {
          header: "Prompt Type",
          id: "empty",
          cell() {
            return <div>No prompt overrides configured</div>;
          },
        },
      ];
    }

    return [
      {
        header: "Prompt Type",
        cell(props) {
          return (
            <div
              className="promptOverrideName"
              onClick={() =>
                navigate(
                  `/admin/prompt_overrides/${props.row.original.id}/edit`
                )
              }
            >
              {PROMPT_TYPE_OPTIONS.find(
                (opt) => opt.value === props.row.original.promptType
              )?.label || props.row.original.promptType}
            </div>
          );
        },
      },
      {
        header: "LLM Key",
        cell(props) {
          return <div>{props.row.original.openRouterKeyName}</div>;
        },
      },
      {
        header: "Content",
        cell(props) {
          const content = props.row.original.content;
          return (
            <div>
              {content.length > 100
                ? `${content.substring(0, 100)}...`
                : content}
            </div>
          );
        },
      },
      {
        header: "Models",
        cell(props) {
          const models = props.row.original.model;
          return <div>{models.length > 0 ? models.join(", ") : ""}</div>;
        },
      },
    ];
  };

  const columns = getColumns();
  const hasData = !!authorized && !isLoading && promptOverrides.length > 0;

  return (
    <div className="promptOverridesTable">
      <Card
        style={{
          padding: "10px",
          borderRadius: "4px",
          border: "1px solid var(--gray-3-stroke, #D7D7D7)",
          background: " var(--white, #FFF)",
          boxShadow: "0px 2px 3px 0px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Card.Header
          style={{
            backgroundColor: "#fff",
            lineHeight: "unset",
            display: "block",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div className="accordianTitle" style={{ margin: "0" }}>
              Prompt Overrides
            </div>
            <Button
              className="squareGreenButton"
              onClick={() => navigate("/admin/prompt_overrides/new")}
            >
              Add a Prompt Override
            </Button>
          </div>
          <hr></hr>
          <div
            style={{
              display: "flex",
            }}
          >
            {selectedIds.length > 0 && (
              <Button
                style={{
                  marginRight: "10px",
                  alignSelf: "flex-end",
                  background: "unset",
                  color: "var(--teal-1, #418172)",
                  fontWeight: "700",
                  width: "11em",
                  height: "40px",
                  borderRadius: "4px",
                  border: "1px solid var(--gray-3-stroke, #D7D7D7)",
                }}
                onClick={handleDelete}
              >
                {selectedIds.length} Selected{" "}
                <span
                  style={{
                    borderLeft: "1px solid #D7D7D7",
                    height: "24px",
                    margin: "0 8px",
                  }}
                ></span>
                <FontAwesomeIcon style={{ color: "black" }} icon={faTrashCan} />
              </Button>
            )}
          </div>
        </Card.Header>
        <DataTable
          data={hasData ? promptOverrides : [{} as PromptOverride]}
          columns={columns}
          selectable={hasData}
          bordered
          striped
          hover
          totalPages={pageMeta.pageCount}
          totalRows={pageMeta.recordCount}
          currentPage={pageNumber}
          setCurrentPage={setPageNumber}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          paginationEnabled={false}
          pageSizeEnabled={false}
        />
      </Card>
      <ConfirmationModal
        title="Confirm Delete"
        message={`Are you sure that you want to delete ${selectedIds.length} prompt override(s)?`}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        show={showConfirmationModal}
        onHide={handleCancel}
      />
    </div>
  );
};

export default List;
